import React from 'react';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFile, faFileWord } from '@fortawesome/free-solid-svg-icons';
import BootstrapTable from '../../react-bootstrap-table-next/react-bootstrap-table2';
import {
    TransactionDocumentVersion,
    TransactionDocumentVersionStage,
} from '../../../models/TransactionDocuments/transaction-documents.model';
import { MeetingTimeZoneHelper } from '../../../utils/meetingTimeZoneHelper';
import { EM_DASH } from '../../../utils/flowOfFundsFormat';
import './TransactionDocumentVersionsTable.scss';

interface TransactionDocumentVersionsTableProps {
    versions: TransactionDocumentVersion[];
    onDownload: (version: TransactionDocumentVersion) => void;
    noDataIndication?: () => JSX.Element;
}

const WORD_FILE_EXTENSION_PATTERN = /\.docx?$/i;

const VersionStageLabels = {
    [TransactionDocumentVersionStage.BuyerDraft]: 'Buyer Draft',
};

const formatUploadedAt = (uploadedAt: string) =>
    `${moment.utc(uploadedAt).local().format('M/D/YYYY, h:mm A')} ${MeetingTimeZoneHelper.getViewerTimeZoneLabel(
        uploadedAt
    )}`;

export const TransactionDocumentVersionsTable = (
    props: TransactionDocumentVersionsTableProps
) => {
    const columns = [
        {
            dataField: 'fileName',
            text: 'Document',
            headerClasses: 'document-column-header',
            formatter: (_: string, version: TransactionDocumentVersion) => (
                <div className="transaction-document-version__document">
                    <FontAwesomeIcon
                        className="transaction-document-version__file-icon"
                        icon={
                            WORD_FILE_EXTENSION_PATTERN.test(version.fileName)
                                ? faFileWord
                                : faFile
                        }
                    />
                    <span
                        className="transaction-document-version__file-link"
                        onClick={() => props.onDownload(version)}>
                        {version.fileName}
                    </span>
                    <div>
                        <span className="badge badge-pill badge-info">
                            {VersionStageLabels[version.stage]}
                        </span>
                    </div>
                </div>
            ),
        },
        {
            dataField: 'documentType',
            text: 'Document Type',
            headerClasses: 'document-type-column-header',
            classes: 'transaction-document-version__document-type',
        },
        {
            dataField: 'uploaderName',
            text: 'Uploading Party',
            headerClasses: 'uploading-party-column-header',
            formatter: (_: string, version: TransactionDocumentVersion) => (
                <div>
                    <div className="transaction-document-version__uploader">
                        {version.uploaderName}
                    </div>
                    {version.uploaderOrganizationName && (
                        <div>{version.uploaderOrganizationName}</div>
                    )}
                    <div className="transaction-document-version__on-behalf-of">
                        on behalf of
                    </div>
                    <div>{version.onBehalfOfName}</div>
                </div>
            ),
        },
        {
            dataField: 'uploadedAt',
            text: 'Date Uploaded',
            headerClasses: 'date-uploaded-column-header',
            formatter: (uploadedAt: string) => formatUploadedAt(uploadedAt),
        },
        {
            dataField: 'comment',
            text: 'Comments',
            headerClasses: 'comments-column-header',
        },
        {
            dataField: 'finalDocument',
            isDummyField: true,
            text: 'Final Document',
            headerClasses: 'final-document-column-header',
            formatter: () => EM_DASH,
        },
    ];

    return (
        <div className="transaction-document-versions-table">
            <BootstrapTable
                bordered={false}
                bootstrap4
                wrapperClasses="table-responsive"
                keyField="id"
                data={props.versions}
                columns={columns}
                noDataIndication={props.noDataIndication}
            />
        </div>
    );
};
